import { cloneElement, FC, forwardRef } from 'react';
import clsx from 'clsx';
import mergewith from 'lodash.mergewith';

import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import BaseStepper from '@material-ui/core/Stepper';

import { IStepper, IStepHeadingProps } from './types';
import useStyles, { StepConnector, Step, StepIcon, StepLabel, StepButton } from './styles';

const Heading: FC<IStepHeadingProps> = ({ step, hideDescription }) => {
  const css = useStyles();
  return (
    <div data-testid="heading" className={css.heading}>
      <label className={css.denseTitle}>{step?.title}</label>
      {!hideDescription && <div className="description">{step?.description}</div>}
    </div>
  );
};

const Stepper: FC<IStepper> = ({
  steps,
  descriptive = true,
  emphasis = false,
  dense = false,
  responsive = true,
  orientation: customOrientation,
  headingPosition = 'top',
  showActiveOnly = false,
  onClickStep = () => null,
  activeStep = 0,
  classes,
  indicatorSize = 'small',
  inlineVariant,
  ...customProps
}) => {
  const css = useStyles();
  const theme = useTheme();

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  const isMobileView = responsive && isSmallScreen;
  const orientation = isMobileView ? 'horizontal' : customOrientation;
  const isDenseEnabled = isMobileView || dense;
  const isVertical = orientation === 'vertical';
  const isHorizontal = orientation === 'horizontal';
  const showHeading = isHorizontal && isDenseEnabled;
  const showHeadingOnTop = showHeading && headingPosition === 'top';
  const showHeadingOnBottom = showHeading && headingPosition === 'bottom';
  const currentStep = steps[activeStep];
  const stepButtonClasess = inlineVariant ? css.stepActive : '';
  const defaultStepperProps = {
    className: clsx(
      css.stepper,
      classes?.stepper,
      {
        [css.dense]: isDenseEnabled,
        [css.vertical]: isVertical,
        [css.horizontal]: isHorizontal,
        [css.emphasis]: emphasis,
        [css.activeOnly]: showActiveOnly,
      },
      {
        [css.indicatorSmall]: indicatorSize === 'small',
        [css.indicatorMedium]: indicatorSize === 'medium',
        [css.indicatorLarge]: indicatorSize === 'large',
      },
    ),
    orientation,
    connector: isVertical ? null : <StepConnector data-testid="default-connector" />,
    activeStep,
  };
  const stepperProps = mergewith(defaultStepperProps, customProps);

  const containerClasses = clsx(css.root, classes?.root);

  const connectorClassNames = clsx(
    'MuiStepConnector-root',
    css.stepConnector,
    classes?.stepConnector,
  );

  const connectorClasses = {
    line: css.stepConnectorLine,
  };

  const stepLabelClasses = {
    labelContainer: clsx(css.stepLabelContainer, classes?.stepLabelContainer),
    iconContainer: clsx(css.stepLabelIconContainer, classes?.stepLabelIconContainer),
  };

  return (
    <div className={containerClasses}>
      {showHeadingOnTop && <Heading step={currentStep} hideDescription={!descriptive} />}
      <BaseStepper data-testid="stepper" {...stepperProps}>
        {steps.map((step, index) => {
          const currentStep = activeStep || 0;
          const isActive = index === currentStep;
          const isCompleted = index < currentStep;

          const stepClasses = clsx(css.step, classes?.step, {
            [css.stepInactive]: !isActive,
          });

          const Connector = forwardRef(() =>
            cloneElement(<StepConnector data-testid="custom-connector" />, {
              active: isActive,
              completed: isCompleted,
              orientation,
              className: connectorClassNames,
              classes: connectorClasses,
              hidden: isHorizontal,
            }),
          );

          return (
            <Step key={`${step.title} - ${index}`} className={stepClasses} role="step">
              <Connector />
              <StepButton
                role="step-button"
                active={isActive}
                completed={isCompleted}
                optional={descriptive && step.description}
                onClick={() => onClickStep(index)}
                className={isActive ? stepButtonClasess : ''}
              >
                <StepLabel
                  role="step-title"
                  classes={stepLabelClasses}
                  StepIconComponent={StepIcon}
                >
                  {step.title}
                </StepLabel>
              </StepButton>
            </Step>
          );
        })}
      </BaseStepper>
      {showHeadingOnBottom && <Heading step={currentStep} hideDescription={!descriptive} />}
    </div>
  );
};

export default Stepper;
